/**
 * ShopContext — global cart, products and session state.
 *
 * Products are now fetched via TanStack Query (useQuery) so the result
 * is shared with every other component that calls useAllProducts().
 * There is exactly ONE network request for /allproducts per session,
 * not two (the old ShopContext fetch + TanStack Query fetch).
 */

import React, { createContext, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useCookieConsent from "../hooks/useCookieConsent";
import { KEYS }         from "../hooks/useForgeQueries";

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

export const ShopContext = createContext(null);

const getDefaultCart = () => {
  const cart = {};
  for (let i = 0; i < 300; i++) cart[i] = 0;
  return cart;
};

const ShopContextProvider = (props) => {
  const consent     = useCookieConsent();
  const queryClient = useQueryClient();

  /* ── Products — shared TanStack Query cache ──────────────────────────────
     useQuery here and useAllProducts() in components hit the SAME cache key
     so only one network request is ever made.                              */
  const {
    data:    products    = [],
    isError: serverDown,
    refetch: retryFetch,
    isFetching: retrying,
  } = useQuery({
    queryKey: KEYS.allProducts,
    queryFn:  () =>
      fetch(`${API}/allproducts`)
        .then((r) => { if (!r.ok) throw new Error("Server error"); return r.json(); }),
    staleTime: 5 * 60 * 1000,   // 5 min — serve cache instantly
    gcTime:    10 * 60 * 1000,  // 10 min — keep in memory
    retry: 1,
  });

  /* ── Packs ───────────────────────────────────────────────────────────── */
  const [packs,        setPacks]       = useState([]);
  const [cartItems,    setCartItems]   = useState(getDefaultCart());
  const [mlSelections, setMlSelections] = useState(() => {
    const saved = localStorage.getItem("forge-cookie-consent");
    if (saved !== "accepted") return {};
    try { return JSON.parse(localStorage.getItem("ml-selections") || "{}"); }
    catch { return {}; }
  });

  useEffect(() => {
    // Load pack config
    fetch(`${API}/packs`)
      .then((r) => r.json())
      .then((data) => { if (data.packs) setPacks(data.packs); })
      .catch(() => {});

    // Load saved cart if logged in
    if (localStorage.getItem("auth-token")) {
      fetch(`${API}/getcart`, {
        method: "POST",
        headers: {
          Accept: "application/form-data",
          "auth-token": localStorage.getItem("auth-token"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(),
      })
        .then((r) => r.json())
        .then((data) => setCartItems(data))
        .catch(() => {});
    }
  }, []);

  /* ── Cart total — pack-price aware ─────────────────────────────────── */
  const getTotalCartAmount = () => {
    let total = 0;
    for (const itemKey in cartItems) {
      const qty = cartItems[itemKey];
      if (qty <= 0) continue;
      const product = products.find((p) => p.id === Number(itemKey));
      if (!product) continue;

      const matchedPack = packs.find((pk) => pk.productId === product.id && pk.qty === qty);
      if (matchedPack) {
        total += matchedPack.price;
      } else {
        let remaining = qty;
        const pack3 = packs.find((pk) => pk.productId === product.id && pk.qty === 3);
        const pack2 = packs.find((pk) => pk.productId === product.id && pk.qty === 2);
        if (pack3) { const sets = Math.floor(remaining / 3); total += sets * pack3.price; remaining -= sets * 3; }
        if (pack2 && remaining >= 2) { const sets = Math.floor(remaining / 2); total += sets * pack2.price; remaining -= sets * 2; }
        total += remaining * product.new_price;
      }
    }
    return Math.round(total);
  };

  const getTotalCartItems = () => {
    let count = 0;
    for (const item in cartItems) {
      if (cartItems[item] > 0 && products.find((p) => p.id === Number(item))) count += cartItems[item];
    }
    return count;
  };

  /* ── Cart mutations ──────────────────────────────────────────────────── */
  const addToCart = (itemId) => {
    const product = products.find((p) => p.id === Number(itemId));
    const stock   = product?.stock;
    const inCart  = cartItems[itemId] || 0;
    if (stock !== undefined && stock !== null && inCart >= stock) return false;
    setCartItems((prev) => ({ ...prev, [itemId]: (prev[itemId] || 0) + 1 }));
    if (localStorage.getItem("auth-token")) {
      fetch(`${API}/addtocart`, { method: "POST", headers: { Accept: "application/form-data", "auth-token": localStorage.getItem("auth-token"), "Content-Type": "application/json" }, body: JSON.stringify({ itemId, qty: 1 }) }).then((r) => r.text()).catch(() => {});
    }
    return true;
  };

  const addToCartBulk = (itemId, qty = 1) => {
    const product = products.find((p) => p.id === Number(itemId));
    const stock   = product?.stock;
    const inCart  = cartItems[itemId] || 0;
    let toAdd = qty;
    if (stock !== undefined && stock !== null) toAdd = Math.min(qty, stock - inCart);
    if (toAdd <= 0) return 0;
    setCartItems((prev) => ({ ...prev, [itemId]: (prev[itemId] || 0) + toAdd }));
    if (localStorage.getItem("auth-token")) {
      fetch(`${API}/addtocart`, { method: "POST", headers: { Accept: "application/form-data", "auth-token": localStorage.getItem("auth-token"), "Content-Type": "application/json" }, body: JSON.stringify({ itemId, qty: toAdd }) }).then((r) => r.text()).catch(() => {});
    }
    return toAdd;
  };

  const clearCart = () => setCartItems(getDefaultCart());

  const removeFromCart = (itemId) => {
    setCartItems((prev) => ({ ...prev, [itemId]: Math.max(0, (prev[itemId] || 0) - 1) }));
    if (localStorage.getItem("auth-token")) {
      fetch(`${API}/removefromcart`, { method: "POST", headers: { Accept: "application/form-data", "auth-token": localStorage.getItem("auth-token"), "Content-Type": "application/json" }, body: JSON.stringify({ itemId }) }).then((r) => r.text()).catch(() => {});
    }
  };

  /* Allow pages to push fresh data into the shared TanStack cache */
  const updateProducts = (data) =>
    queryClient.setQueryData(KEYS.allProducts, data);

  const setMlSelection = (productId, ml) => {
    setMlSelections((prev) => {
      const next = { ...prev, [productId]: ml };
      if (consent === "accepted") { try { localStorage.setItem("ml-selections", JSON.stringify(next)); } catch {} }
      return next;
    });
  };

  useEffect(() => {
    if (consent === "rejected") setMlSelections({});
  }, [consent]);

  const contextValue = {
    products, packs, setPacks, cartItems, mlSelections,
    serverDown, retrying, retryFetch,
    addToCart, addToCartBulk, removeFromCart, clearCart,
    getTotalCartItems, getTotalCartAmount,
    updateProducts, setMlSelection,
  };

  return (
    <ShopContext.Provider value={contextValue}>
      {props.children}
    </ShopContext.Provider>
  );
};

export default ShopContextProvider;
